// ** MUI Imports
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'

// ** React Imports
import { useEffect, memo } from 'react'

// ** Icons
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import LocationSearchingIcon from '@mui/icons-material/LocationSearching'

// ** Types
import { CekRiwayatHidupJSON } from 'src/types/pelamar-identitas'

interface CorrectionsTableProps {
    data: CekRiwayatHidupJSON[]
    onViewClick?: (row: CekRiwayatHidupJSON) => void
    onFixClick?: (row: CekRiwayatHidupJSON) => void
}

// Common button style base
const commonButtonStyle = {
    borderRadius: 4,
    textTransform: 'none !important',
}

// View button styles
const viewButtonStyles = () => ({
    ...commonButtonStyle,
    border: (theme: any) => `1.5px solid ${theme.myColor?.colorToggle}`,
    '&:hover': {
        border: (theme: any) => `1.5px solid ${theme.myColor?.colorToggle}`,
        color: (theme: any) => theme.myColor?.colorToggle,
        backgroundColor: (theme: any) => `${theme.myColor?.colorToggleLight} !important`
    }
})

// Fixed button styles
const fixedButtonStyles = () => ({
    ...commonButtonStyle,
    width: 'max-content',
    border: (theme: any) => `1.5px solid ${theme.palette.success.main}`,
    '&:hover': {
        border: (theme: any) => `1.5px solid ${theme.palette.success.main}`,
        color: (theme: any) => theme.palette.success.main,
        backgroundColor: (theme: any) => `${theme.palette.success.light} !important`
    }
})

// Highlight button styles
const highlightButtonStyles = () => ({
    ...commonButtonStyle,
    border: (theme: any) => `1.5px solid ${theme.palette.info.main}`,
    '&:hover': {
        border: (theme: any) => `1.5px solid ${theme.palette.info.main}`,
        color: (theme: any) => theme.palette.info.main,
        backgroundColor: (theme: any) => `${theme.palette.info.light} !important`
    }
})

// Status cell renderer
const renderStatusCell = (status: string) => {
    switch (status) {
        case 'new':
            return <ErrorOutlineRoundedIcon color='error' sx={{ width: 16, height: 16, mr: 0.5 }} />
        case 'pending':
            return <AccessTimeRoundedIcon color='info' sx={{ width: 16, height: 16, mr: 0.5 }} />
        default:
            return <TaskAltRoundedIcon color='success' sx={{ width: 16, height: 16, mr: 0.5 }} />
    }
}

// Memoized table row component for better performance
const CorrectionRow = memo(({
    row,
    index,
    onViewClick,
    onFixClick,
    highlightComponent
}: {
    row: CekRiwayatHidupJSON,
    index: number,
    onViewClick?: (row: CekRiwayatHidupJSON) => void,
    onFixClick?: (row: CekRiwayatHidupJSON) => void,
    highlightComponent: (id: string) => void
}) => {

    return (
        <TableRow>
            <TableCell align='center'>{index + 1}</TableCell>
            <TableCell align='center'>{renderStatusCell(row.status)}</TableCell>
            <TableCell component='th' scope='row' align='left'>
                <Typography
                    variant='body2'
                    sx={{ color: theme => theme.myColor?.colorToggle, fontWeight: 600 }}
                >
                    {row.title}
                </Typography>
                <Typography variant='caption' sx={{ fontStyle: 'italic' }}>
                    {row.content}
                </Typography>
            </TableCell>

            <TableCell align='center'>
                {onViewClick && onFixClick ? (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
                    >
                        <Button
                            variant='outlined'
                            size='small'
                            onClick={() => onViewClick(row)}
                            sx={viewButtonStyles}
                        >
                            Lihat
                        </Button>
                        <Button
                            variant='outlined'
                            size='small'
                            color='success'
                            disabled={row.status !== 'new'}
                            onClick={() => onFixClick(row)}
                            sx={fixedButtonStyles}
                        >
                            <DoneAllRoundedIcon sx={{ mr: 1, width: 16, height: 16 }} />
                            Sudah diperbaiki
                        </Button>
                    </Box>
                ) : (
                    row.target && (
                        <Box sx={{ mt: 1 }}>
                            <Button
                                variant='outlined'
                                size='small'
                                color='info'
                                onClick={() => highlightComponent(row.target)}
                                sx={highlightButtonStyles}
                                startIcon={<LocationSearchingIcon sx={{ width: 16, height: 16 }} />}
                            >
                                Lihat
                            </Button>
                        </Box>
                    )
                )}
            </TableCell>
        </TableRow>
    );
});

const CorrectionsTable = ({ data, onViewClick, onFixClick }: CorrectionsTableProps) => {
    // Add highlight styles on component mount
    useEffect(() => {
        const styleId = 'highlight-pulse-styles';
        if (!document.getElementById(styleId)) {
            const styleSheet = document.createElement('style');
            styleSheet.id = styleId;
            styleSheet.innerHTML = `
                .highlight-pulse {
                    animation: simplePulse 1s infinite;
                    background-color: gray;
                    position: absolute;
                    border-radius: 8px;
                    z-index: 1000;
                    pointer-events: none;
                }

                .highlight-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.6);
                    z-index: 1000;
                    transition: opacity 0.3s ease;
                    pointer-events: none;
                    mix-blend-mode: hard-light;
                }

                @keyframes simplePulse {
                    0% { box-shadow: 0 0 10px 5px rgba(255, 255, 255, 0.4); }
                    50% { box-shadow: 0 0 15px 10px rgba(255, 255, 255, 0.6); }
                    100% { box-shadow: 0 0 10px 5px rgba(255, 255, 255, 0.4); }
                }
            `;
            document.head.appendChild(styleSheet);
        }

        // Cleanup function
        return () => {
            // Remove overlay if component unmounts
            const overlay = document.getElementById('highlight-overlay');
            if (overlay) overlay.remove();
        };
    }, []);

    // Optimized function to highlight a component by ID
    const highlightComponent = (targetId: string) => {
        if (!targetId) return;

        // Remove any existing highlight
        const existingOverlay = document.getElementById('highlight-overlay');
        if (existingOverlay) existingOverlay.remove();

        // Clean ID and find element
        const cleanId = targetId.startsWith('#') ? targetId.substring(1) : targetId;
        const targetElement = document.getElementById(cleanId);

        if (!targetElement) {
            console.warn(`Element with ID ${cleanId} not found for highlighting`);

            return;
        }

        // Create overlay and spotlight
        const overlay = document.createElement('div');
        overlay.id = 'highlight-overlay';
        overlay.className = 'highlight-overlay';

        const spotlight = document.createElement('div');
        spotlight.className = 'highlight-pulse';

        // Position spotlight
        const gap = 5;
        const updatePosition = () => {
            if (!targetElement) return;

            const rect = targetElement.getBoundingClientRect();
            spotlight.style.top = `${rect.top - gap}px`;
            spotlight.style.left = `${rect.left - gap}px`;
            spotlight.style.width = `${rect.width + (gap * 2)}px`;
            spotlight.style.height = `${rect.height + (gap * 2)}px`;
        };

        updatePosition();
        overlay.appendChild(spotlight);
        document.body.appendChild(overlay);

        // Scroll to element
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Add event listeners
        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);

        // Auto clear highlight
        setTimeout(() => {
            overlay.style.opacity = '0';

            setTimeout(() => {
                overlay.remove();
                window.removeEventListener('scroll', updatePosition);
                window.removeEventListener('resize', updatePosition);
            }, 300);
        }, 2000);
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align='center'>No</TableCell>
                        <TableCell align='center'>Status</TableCell>
                        <TableCell align='left'>Keterangan</TableCell>
                        <TableCell align='center'>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <CorrectionRow
                            key={'corr' + index}
                            row={row}
                            index={index}
                            onViewClick={onViewClick}
                            onFixClick={onFixClick}
                            highlightComponent={highlightComponent}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(CorrectionsTable);
